import { Box, Flex } from '@chakra-ui/layout';
import { Button, Card, CardBody, Checkbox } from '@chakra-ui/react';
import React, { FormEventHandler, useCallback, useMemo, useState } from 'react';

import { useVisibleOrganizationsQuery } from '../implementations.gql.generated';
import { Alert } from 'packages/common-frontend/components/Alert';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { VirtualizedList } from '~/core/routes/settings/implementations/common/VirtualizedList';
import { WizardComponent } from '~/core/routes/settings/implementations/create/ImplementationWizard';

export const ChooseOrganizations: WizardComponent = ({
  selectedOrgs,
  setSelectedOrgs,
  nextStep,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user } = useUserAuthOrThrow();
  const { data, loading, error } = useVisibleOrganizationsQuery({
    variables: { organizationId: user.organizationId },
  });

  if (error) throw error;

  const [searchQuery, setSearchQuery] = useState('');

  const orgs = useMemo(() => data?.organization.visibleOrganizations || [], [
    data,
  ]);

  const onFormChange: FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      const target = event.target;

      if (target instanceof HTMLInputElement) {
        const orgId = target.value;
        const selectedOrg = orgs.find((org) => org.id === orgId);

        if (target.checked && selectedOrg) {
          setSelectedOrgs((prevSelectedOrgs) => [
            ...prevSelectedOrgs,
            selectedOrg,
          ]);
        } else {
          setSelectedOrgs((prevSelectedOrgs) =>
            prevSelectedOrgs.filter((prevOrg) => prevOrg.id !== orgId),
          );
        }
      }
    },
    [orgs, setSelectedOrgs],
  );

  const filteredData = useMemo(
    () =>
      orgs.filter(
        (org) =>
          org.name.toLowerCase().includes(searchQuery) &&
          !selectedOrgs.find((selectedOrg) => selectedOrg.id === org.id),
      ),
    [selectedOrgs, searchQuery, orgs],
  );

  const isInvalid = selectedOrgs.length < 2;

  return (
    <Flex direction="column" width="100%" gap="1rem">
      <Box>
        <Typography variant="page-title">Set up your Facility</Typography>
        <Typography variant="base-muted">
          Your implementation will start with a structured approach to establish
          your initial facility. Start by adding organizations to your facility.
        </Typography>
      </Box>
      <Flex gap="1.5rem">
        <Card
          width="50%"
          variant="outline"
          size="lg"
          data-testid="unselected-organizations"
        >
          <CardBody>
            <Box mb="0.5rem">
              <Typography variant="base" fontWeight="bold">
                Select Organizations
              </Typography>
            </Box>
            <SearchBarInput
              query={searchQuery}
              onChangeQuery={(value) => setSearchQuery(value.toLowerCase())}
            />
            <form onChange={onFormChange}>
              <VirtualizedList
                list={filteredData}
                isLoading={loading}
                renderListItem={(item) => (
                  <Box
                    width="100%"
                    borderWidth="thin"
                    px="1em"
                    py="0.6rem"
                    my="0.2rem"
                    borderRadius="8px"
                  >
                    <Checkbox value={item.id} width="100%">
                      {item.name}
                    </Checkbox>
                  </Box>
                )}
              />
            </form>
          </CardBody>
        </Card>
        <Card
          width="50%"
          variant="outline"
          size="lg"
          data-testid="selected-organizations"
        >
          <CardBody>
            <Typography variant="base" fontWeight="bold">
              Selected
            </Typography>
            <form onChange={onFormChange}>
              {selectedOrgs.map((org) => (
                <Box
                  borderWidth="thin"
                  px="1em"
                  py="0.6rem"
                  my="0.4rem"
                  borderRadius="8px"
                  key={org.id}
                >
                  <Checkbox value={org.id} defaultChecked>
                    {org.name}
                  </Checkbox>
                </Box>
              ))}
            </form>
          </CardBody>
        </Card>
      </Flex>
      {isInvalid && isSubmitted && (
        <Alert status="error">
          <Typography variant="base" fontWeight="bold">
            Select at least two organizations. One borrower and one lender are
            required to set up the facility.
          </Typography>
        </Alert>
      )}
      <Flex justify="end">
        <Button
          type="button"
          variant="primary"
          colorScheme="v2-aurora"
          onClick={() => {
            setIsSubmitted(true);
            if (!isInvalid) {
              nextStep();
            }
          }}
        >
          Next
        </Button>
      </Flex>
    </Flex>
  );
};
